import { createFileRoute } from '@tanstack/react-router';
import {
  Bot,
  CalendarClock,
  Gavel,
  History,
  PauseCircle,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { CopyableId } from '@/components/primitives/CopyableId';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import {
  buildBoard,
  DRAFT_AUDIT_LABELS,
  formatDuration,
  useDraft,
  useDraftAudit,
  type DraftAuditEntry,
  type DraftOrderRow,
  type DraftPayload,
  type DraftPick,
} from '@/features/leagues/draft';
import { ApiError } from '@/lib/api/client';

export const Route = createFileRoute('/leagues/$leagueId/draft')({
  component: DraftTab,
});

/**
 * Live draft monitor.
 *
 * READ-ONLY, and that is a scope decision rather than an omission. All thirteen
 * draft overrides are registered delegate operations, but ZazzBackend's commissioner
 * guards still 403 an admin operator (OPEN_QUESTIONS item 2), so every control this
 * screen could offer would refuse. Watching is the part that works today, and during
 * a live draft it is also the part that matters: a stalled clock is invisible
 * everywhere else until members start reporting it.
 */
function DraftTab() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isError, error, refetch, isFetching, dataUpdatedAt } =
    useDraft(leagueId);

  if (isError) {
    // A league that never configured a draft is an answer, not a failure.
    if (
      error instanceof ApiError &&
      (error.code === 'NO_DRAFT' || error.code === 'NO_ACTIVE_SEASON')
    ) {
      return (
        <StateBlock
          icon={CalendarClock}
          tone="neutral"
          title={error.code === 'NO_DRAFT' ? 'No draft configured' : 'No active season'}
          description={
            error.code === 'NO_DRAFT'
              ? 'This season has no draft settings document, so a draft has never been scheduled for it.'
              : 'This league has no active season, so there is no draft to show.'
          }
        />
      );
    }
    return <ErrorState error={error} subject="the draft" onRetry={() => void refetch()} />;
  }
  if (isPending) return <Skeleton height={260} />;

  return (
    <div className="flex flex-col gap-4">
      <ClockPanel
        data={data}
        isFetching={isFetching}
        dataUpdatedAt={dataUpdatedAt}
        onRefresh={() => void refetch()}
      />
      <RecentPicks picks={data.picks} />
      <Board data={data} />
      <OrderPanel data={data} />
      <AuditPanel leagueId={leagueId} />
    </div>
  );
}

/* ------------------------------------------------------------------ clock */

function ClockPanel({
  data,
  isFetching,
  dataUpdatedAt,
  onRefresh,
}: {
  data: DraftPayload;
  isFetching: boolean;
  dataUpdatedAt: number;
  onRefresh: () => void;
}) {
  /**
   * Ticks locally between polls.
   *
   * The countdown must move every second or the screen reads as frozen — which is
   * precisely the thing an operator is here to distinguish from a frozen DRAFT. The
   * value is always derived from the server's `pickDeadline`, so a local clock skew
   * shows as a wrong countdown rather than a wrong decision.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const { state, settings, health, progress } = data;
  const deadline = state?.pickDeadline ? new Date(state.pickDeadline).getTime() : null;
  const remainingMs = deadline !== null ? deadline - now : null;

  return (
    <section
      className="rounded-xl border p-3.5"
      style={
        health.isStalled
          ? {
              borderColor: 'rgba(var(--err-rgb), 0.42)',
              background: 'rgba(var(--err-rgb), 0.07)',
            }
          : { borderColor: 'var(--bd)', background: 'var(--card)' }
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2
            className="font-head text-tx2 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
            style={{ letterSpacing: '0.16em' }}
          >
            <Gavel size={12} aria-hidden />
            Draft
          </h2>
          {state?.status ? (
            <StateChip state={state.status} />
          ) : (
            <StateChip state={settings.status ?? 'not_scheduled'} />
          )}
          {state?.pickStatus && state.status === 'in_progress' && (
            <StateChip state={state.pickStatus} size="sm" />
          )}
          <CopyableId value={data.draftId} label="Draft ID" truncate />
        </div>

        <LiveDataIndicator
          updatedAt={dataUpdatedAt || undefined}
          isFetching={isFetching}
          isLive
          // Tighter than anywhere else in the console: a draft clock read from
          // 45-second-old data is a clock read wrong.
          staleAfterMs={15_000}
          onRefresh={onRefresh}
        />
      </div>

      {health.isStalled && (
        <p
          className="mt-2.5 flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px]"
          style={{
            borderColor: 'rgba(var(--err-rgb), 0.42)',
            background: 'rgba(var(--err-rgb), 0.1)',
            color: 'var(--err-soft)',
          }}
        >
          <TriangleAlert size={15} className="mt-px flex-none" aria-hidden />
          <span>
            <strong className="font-semibold">
              Draft stalled — clock expired {formatDuration(health.overdueMs)} ago.
            </strong>{' '}
            The pick-expiration job should have advanced this. Nothing in the app will surface
            it to members; they simply see a clock at zero.
          </span>
        </p>
      )}

      {health.statusMismatch && (
        <p
          className="mt-2 rounded-lg border px-3 py-2 text-[12.5px]"
          style={{
            borderColor: 'rgba(var(--warn-rgb), 0.4)',
            background: 'rgba(var(--warn-rgb), 0.08)',
            color: 'var(--warn)',
          }}
        >
          Settings say <span className="mesh-mono">{settings.status}</span> while the live state
          says <span className="mesh-mono">{state?.status}</span>. A settings document left at{' '}
          <span className="mesh-mono">scheduled</span> can mean the scheduled-start job is still
          pending.
        </p>
      )}

      {state?.status === 'paused' && (
        <p
          className="mt-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-[12.5px]"
          style={{
            borderColor: 'rgba(var(--warn-rgb), 0.4)',
            background: 'rgba(var(--warn-rgb), 0.08)',
            color: 'var(--warn)',
          }}
        >
          <PauseCircle size={14} className="mt-px flex-none" aria-hidden />
          <span>
            Paused{state.pausedByName ? ` by ${state.pausedByName}` : ''}
            {state.pausedAt ? ` at ${state.pausedAt.replace('T', ' ').slice(0, 16)}Z` : ''}
            {state.pausedReason ? ` — "${state.pausedReason}"` : ''}
            {typeof state.pausedRemainingMs === 'number'
              ? `. ${formatDuration(state.pausedRemainingMs)} was banked on the clock.`
              : ''}
          </span>
        </p>
      )}

      {state ? (
        <dl className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <Label>On the clock</Label>
            <dd className="text-tx mt-0.5 truncate text-[15px] font-semibold">
              {state.currentSquadName ?? '—'}
            </dd>
          </div>
          <div className="min-w-0">
            <Label>Time left</Label>
            <dd
              className="mesh-mono mt-0.5 text-[15px] font-semibold"
              style={{
                color:
                  remainingMs === null || state.status !== 'in_progress'
                    ? 'var(--tx3)'
                    : remainingMs <= 0
                      ? 'var(--err)'
                      : remainingMs < 15_000
                        ? 'var(--warn)'
                        : 'var(--tx)',
              }}
            >
              {state.status !== 'in_progress'
                ? '—'
                : remainingMs === null
                  ? 'no deadline'
                  : remainingMs <= 0
                    ? `-${formatDuration(-remainingMs)}`
                    : formatDuration(remainingMs)}
            </dd>
          </div>
          <Field
            label="Pick"
            value={`#${String(state.pickNumber ?? '?')} · round ${String(state.currentRound ?? '?')}`}
          />
          <Field
            label="Progress"
            value={`${String(progress.picksMade)} / ${progress.totalPicks > 0 ? String(progress.totalPicks) : '?'} picks`}
          />
        </dl>
      ) : (
        <dl className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Scheduled"
            value={
              settings.draftTime
                ? settings.draftTime.replace('T', ' ').slice(0, 16) + 'Z'
                : 'not set'
            }
          />
          <Field label="Auto start" value={settings.autoStart ? 'yes' : 'no'} />
          <Field
            label="Requires full league"
            value={settings.requireFullLeague ? 'yes' : 'no'}
          />
          <Field label="Squads" value={String(progress.squadCount)} />
        </dl>
      )}

      <dl className="border-bd/60 mt-3 grid gap-x-4 gap-y-3 border-t pt-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Format"
          value={`${settings.draftType ?? '?'} · ${String(settings.rounds ?? '?')} rounds · ${String(settings.secondsPerPick ?? '?')}s`}
        />
        <Field label="Order mode" value={settings.orderMode ?? 'not set'} />
        <Field
          label="Order visible to members"
          // A sealed order is visible to THIS console and to nobody else. Reading a
          // slot back to a member who is not meant to have it yet is a real mistake.
          value={settings.orderMode === 'random' && !settings.orderOpenedAt ? 'sealed' : 'yes'}
        />
        <Field
          label="Autopicks"
          value={`${String(progress.autoPicks)} of ${String(progress.picksMade)}`}
        />
      </dl>

      {!state && (
        <p className="text-tx3 mt-3 text-[12.5px]">
          The draft has not started, so there is no live state document yet. The order and
          settings below are what it will start with.
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ recent picks */

function RecentPicks({ picks }: { picks: DraftPick[] }) {
  // Newest first: during a live draft the last few picks are the whole question.
  const recent = [...picks].sort((a, b) => b.pickNumber - a.pickNumber).slice(0, 12);

  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <SectionHeading icon={History} count={picks.length}>
        Latest picks
      </SectionHeading>

      {recent.length === 0 ? (
        <p className="text-tx3 text-[12.5px]">No picks yet.</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {recent.map((pick) => (
            <li
              key={pick.pickNumber}
              className="border-bd/60 flex items-center gap-2.5 border-b pb-1.5 last:border-b-0 last:pb-0"
            >
              <span className="mesh-mono text-tx3 w-14 flex-none text-[11.5px]">
                {pick.round}.{String(pick.pickNumber).padStart(2, '0')}
              </span>
              <span className="text-tx min-w-0 flex-1 truncate text-[13px]">
                {pick.playerName ?? `player ${String(pick.playerId ?? '?')}`}
                {pick.position && (
                  <span className="text-tx3 mesh-mono ml-1.5 text-[11px]">{pick.position}</span>
                )}
              </span>
              <span className="text-tx2 hidden min-w-0 flex-1 truncate text-[12.5px] sm:block">
                {pick.squadName ?? '—'}
              </span>
              {pick.isAutoPick && (
                <StateChip
                  state="auto"
                  size="sm"
                  tone="warn"
                  // The source is the only durable trace of WHY an autopick chose
                  // what it chose — votes are deleted once a player is drafted.
                  label={pick.autoPickSource === 'vote' ? 'Auto · vote' : 'Auto · ADP'}
                />
              )}
              <span className="mesh-mono text-tx3 hidden w-12 flex-none text-right text-[11px] md:block">
                {pick.at ? pick.at.slice(11, 16) : '—'}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ board */

function Board({ data }: { data: DraftPayload }) {
  const rounds = data.settings.rounds ?? 0;
  const board = buildBoard(data.order, data.picks, rounds);
  const onClockSquad = data.state?.currentSquadId ?? null;
  const onClockRound = data.state?.currentRound ?? null;

  if (data.order.length === 0 || rounds === 0) {
    return (
      <section className="border-bd bg-card rounded-xl border p-3.5">
        <SectionHeading icon={Gavel}>Board</SectionHeading>
        <p className="text-tx3 text-[12.5px]">
          No draft order or round count is set, so there is no grid to draw.
        </p>
      </section>
    );
  }

  return (
    <section className="border-bd bg-card overflow-hidden rounded-xl border">
      <div className="p-3.5 pb-2.5">
        <SectionHeading icon={Gavel}>Board</SectionHeading>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-left">
          <thead>
            <tr className="border-bd bg-elev border-b">
              <th
                scope="col"
                className="font-head text-tx3 px-3 py-2 text-[10.5px] font-semibold uppercase"
                style={{ letterSpacing: '0.14em' }}
              >
                Rd
              </th>
              {data.order.map((squad) => (
                <th
                  key={squad.squadId}
                  scope="col"
                  className="font-head text-tx3 max-w-[150px] truncate px-3 py-2 text-[10.5px] font-semibold uppercase"
                  style={{ letterSpacing: '0.14em' }}
                >
                  <span className="mesh-mono text-tx3 mr-1">{squad.draftSlot ?? '–'}</span>
                  {squad.name}
                  {squad.autoPickEnabled && (
                    <Bot
                      size={11}
                      className="ml-1 inline align-[-1px]"
                      aria-label="autopicking"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {board.map((row, roundIndex) => (
              <tr key={roundIndex} className="border-bd/60 border-b last:border-b-0">
                <td className="mesh-mono text-tx3 px-3 py-1.5 text-[11.5px]">
                  {roundIndex + 1}
                </td>
                {/* Iterated over the ORDER, not the row: the columns are the draft
                    order, and a cell is whatever pick (if any) landed in it. */}
                {data.order.map((squad, columnIndex) => {
                  const pick = row[columnIndex] ?? null;
                  const isOnClock =
                    onClockSquad === squad.squadId && onClockRound === roundIndex + 1 && !pick;
                  return (
                    <td
                      key={squad.squadId}
                      className="max-w-[150px] px-3 py-1.5"
                      style={
                        isOnClock ? { background: 'rgba(var(--info-rgb), 0.14)' } : undefined
                      }
                    >
                      {pick ? (
                        <div className="min-w-0">
                          <div className="text-tx truncate text-[12.5px]">
                            {pick.playerName ?? `#${String(pick.playerId ?? '?')}`}
                          </div>
                          <div className="mesh-mono text-tx3 truncate text-[10.5px]">
                            {pick.position ?? '—'} · {pick.pickNumber}
                            {pick.isAutoPick && ' · auto'}
                          </div>
                        </div>
                      ) : isOnClock ? (
                        <span className="text-info-soft text-[11.5px] font-semibold uppercase">
                          on the clock
                        </span>
                      ) : (
                        <span className="text-tx3 text-[12.5px]">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ order */

function OrderPanel({ data }: { data: DraftPayload }) {
  const overrides = data.state?.pickOwnerOverrides ?? [];

  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <SectionHeading icon={CalendarClock} count={data.order.length}>
        Draft order
      </SectionHeading>

      <ol className="flex flex-col gap-1.5">
        {data.order.map((squad) => (
          <OrderRow
            key={squad.squadId}
            squad={squad}
            isOnClock={data.state?.currentSquadId === squad.squadId}
          />
        ))}
      </ol>

      {overrides.length > 0 && (
        <div className="border-bd/60 mt-3 border-t pt-3">
          <p className="text-tx2 mb-1.5 text-[12.5px]">
            {/* Without this, a pick landing on the "wrong" squad reads as a bug. */}
            {overrides.length} pick{overrides.length === 1 ? ' has' : 's have'} had their owner
            reassigned by a commissioner swap, so the snake order will not predict them.
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {overrides.map((override) => (
              <li
                key={override.pickNumber}
                className="border-bd bg-elev mesh-mono text-tx2 rounded-lg border px-2.5 py-1 text-[11.5px]"
              >
                pick {override.pickNumber} → {override.squadId?.slice(-6) ?? '?'}
                {override.reason ? ` · ${override.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function OrderRow({ squad, isOnClock }: { squad: DraftOrderRow; isOnClock: boolean }) {
  return (
    <li className="border-bd/60 flex items-center gap-2.5 border-b pb-1.5 last:border-b-0 last:pb-0">
      <span
        aria-hidden
        className="mesh-mono grid size-6 flex-none place-items-center rounded text-[11px] font-semibold"
        style={{
          background: 'rgba(var(--brand-rgb), 0.18)',
          color: 'var(--brand-soft)',
        }}
      >
        {squad.draftSlot ?? '–'}
      </span>
      <span className="text-tx min-w-0 flex-1 truncate text-[13px]">{squad.name}</span>
      {isOnClock && <StateChip state="on_the_clock" size="sm" />}
      {squad.isVacant && <StateChip state="vacant" size="sm" tone="warn" label="Vacant" />}
      {squad.autoPickEnabled && (
        <StateChip
          state="autopick"
          size="sm"
          tone="warn"
          // Sticky autopick means nobody on this squad is choosing any more.
          label={squad.autoPickReason ? `Auto · ${squad.autoPickReason}` : 'Autopicking'}
        />
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ audit */

function AuditPanel({ leagueId }: { leagueId: string }) {
  const { data, isPending, isError, error, refetch } = useDraftAudit(leagueId);

  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <SectionHeading icon={ShieldAlert} count={data?.total}>
        Commissioner audit trail
      </SectionHeading>

      {isError ? (
        <ErrorState
          error={error}
          subject="the draft audit trail"
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton height={80} />
      ) : data.entries.length === 0 ? (
        <p className="text-tx3 text-[12.5px]">
          No commissioner overrides have been applied to this draft.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {data.entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
        </ol>
      )}

      <p className="text-tx3 mt-3 text-[11.5px]">
        {/* Two trails exist and neither is complete alone. */}
        This is ZazzBackend's own trail, so it includes overrides a commissioner applied in the
        app. Actions taken through this console also appear under Audit.
      </p>
    </section>
  );
}

function AuditRow({ entry }: { entry: DraftAuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = entry.before !== null || entry.after !== null || entry.metadata !== null;

  return (
    <li className="border-bd/60 border-b pb-2 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-tx text-[13px] font-medium">
          {DRAFT_AUDIT_LABELS[entry.action] ?? entry.action}
        </span>
        <span className="mesh-mono text-tx3 text-[11px]">{entry.action}</span>
        <span className="text-tx2 text-[12px]">{entry.actorName ?? 'unknown actor'}</span>
        <span className="mesh-mono text-tx3 text-[11px]">
          {entry.at ? entry.at.replace('T', ' ').slice(0, 16) + 'Z' : '—'}
        </span>
        {hasDetail && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="border-bd bg-elev text-tx2 hover:bg-high ml-auto rounded-md border px-2 py-0.5 text-[11px]"
          >
            {expanded ? 'Hide' : 'Detail'}
          </button>
        )}
      </div>
      {entry.reason && <p className="text-tx2 mt-0.5 text-[12.5px]">“{entry.reason}”</p>}
      {expanded && (
        <pre className="border-bd bg-bg-alt text-tx2 mt-1.5 overflow-x-auto rounded-lg border p-2 font-mono text-[11px] whitespace-pre-wrap">
          {JSON.stringify(
            { before: entry.before, after: entry.after, metadata: entry.metadata },
            null,
            2,
          )}
        </pre>
      )}
    </li>
  );
}

/* --------------------------------------------------------------- fragments */

function SectionHeading({
  icon: Icon,
  count,
  children,
}: {
  icon: typeof Gavel;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <h2
      className="font-head text-tx2 mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
      style={{ letterSpacing: '0.16em' }}
    >
      <Icon size={12} aria-hidden />
      {children}
      {typeof count === 'number' && (
        <span className="mesh-mono text-tx3 ml-0.5 text-[11px]">{count}</span>
      )}
    </h2>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <dt
      className="font-head text-tx3 text-[10px] font-semibold uppercase"
      style={{ letterSpacing: '0.14em' }}
    >
      {children}
    </dt>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <Label>{label}</Label>
      <dd className="mesh-mono text-tx2 mt-0.5 truncate text-[12.5px]">{value}</dd>
    </div>
  );
}
